import React, { useState } from "react";
import { DEMO_VIDEOS, DEMO_FILTERS } from "../data";
import { SectionLabel } from "../components/ui/Typography";
import { BtnOrange } from "../components/ui/Buttons";
import useScrollFade from "../hooks/useScrollFade";

/* ── Interactive mini chart ── */
const MiniChart = () => {
  const [bars, setBars] = useState([40, 55, 70, 90, 100, 85, 75, 60, 45]);
  const [kpis, setKpis] = useState({ v: "2,847", r: "₨1.2M", c: "32%", d: "4.2m" });

  const refresh = () => {
    setBars(bars.map(() => Math.floor(Math.random() * 70 + 20)));
    setKpis({
      v: (Math.floor(Math.random() * 1000) + 2000).toLocaleString(),
      r: "₨" + (Math.random() * 0.8 + 0.8).toFixed(1) + "M",
      c: Math.floor(Math.random() * 15 + 22) + "%",
      d: (Math.random() * 2 + 3).toFixed(1) + "m",
    });
  };

  const kpiList = [
    { val: kpis.v, label: "Today's Visitors", chg: "↑ 14.2%", up: true },
    { val: kpis.r, label: "Today's Revenue",  chg: "↑ 8.7%",  up: true },
    { val: kpis.c, label: "Conversion Rate",  chg: "↑ 3.1%",  up: true },
    { val: kpis.d, label: "Avg Dwell Time",   chg: "↓ 0.3%",  up: false },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {kpiList.map(k => (
          <div key={k.label} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="font-head font-bold text-xl text-slate-900">{k.val}</div>
            <div className="text-slate-400 text-xs mt-1">{k.label}</div>
            <div className={`text-xs font-semibold mt-1 ${k.up ? "text-emerald-600" : "text-red-500"}`}>{k.chg}</div>
          </div>
        ))}
      </div>
      <div className="font-head font-semibold text-slate-600 text-sm mb-3">Hourly Footfall</div>
      <div
        className="flex items-end gap-2 h-20 cursor-pointer group"
        onClick={refresh}
        title="Click to refresh"
      >
        {bars.map((b, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-md transition-all duration-500 group-hover:opacity-80"
            style={{
              height: `${b}%`,
              background:
                b === Math.max(...bars) ? "#f57c00" :
                b > 65 ? "#1e56d9" : "#cde4ff",
            }}
          />
        ))}
      </div>
      <p className="text-center text-slate-400 text-xs mt-3">Click chart to simulate live data refresh</p>
    </div>
  );
};

/* ── Video card ── */
const VideoCard = ({ video }) => {
  const [playing, setPlaying] = useState(false);
  const ref = useScrollFade();

  return (
    <div ref={ref} className="fade-in-section card-hover bg-white border border-slate-200 rounded-2xl overflow-hidden">
      
      <div className={`aspect-video bg-gradient-to-br ${video.gradient} relative flex items-center justify-center overflow-hidden`}>

        {!playing ? (
          <>
            <button
              onClick={() => setPlaying(true)}
              className="w-14 h-14 rounded-full bg-white/15 border-2 border-white/40 flex items-center justify-center hover:bg-white hover:border-white transition-all duration-200 group"
            >
              ▶
            </button>

            <div className="absolute bottom-3 left-3 right-3 flex justify-between">
              <span className={`${video.tagClass} text-white text-xs font-head font-bold px-2.5 py-1 rounded-md`}>
                {video.product}
              </span>
              <span className="bg-black/40 text-white text-xs px-2 py-1 rounded-md">
                {video.duration}
              </span>
            </div>
          </>
        ) : (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            controls
            autoPlay
          >
            <source src="/footfall.mp4" type="video/mp4" />
          </video>
        )}

      </div>

      <div className="p-5">
        <span className="inline-block text-xs font-head font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full mb-2">
          {video.cat === "retail"
            ? "Retail Intelligence"
            : video.cat === "erp"
            ? "ERP Solutions"
            : "AI Vision"}
        </span>

        <h3 className="font-head font-bold text-slate-900 mb-2">{video.title}</h3>
        <p className="text-slate-500 text-sm leading-relaxed">{video.desc}</p>
      </div>
    </div>
  );
};

/* ── Demo Request form ── */
const DemoRequestForm = () => {
  const [done, setDone] = useState(false);
  return (
    <div className="bg-gradient-to-br from-slate-950 to-[#0d2151] rounded-2xl p-8 md:p-10 grid md:grid-cols-2 gap-10 items-center mt-12">
      <div>
        <h3 className="font-head font-bold text-2xl text-white mb-3">Request a Personalized Demo</h3>
        <p className="text-white/60 text-base leading-relaxed">
          See our products tailored to your specific industry, use case, and data. Our team will walk you through everything live.
        </p>
      </div>
      {done ? (
        <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-xl p-5 text-emerald-400 font-head font-semibold text-center">
          ✅ Demo Request Sent! We'll reach out within 24 hours.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Your Name"     className="px-4 py-3 rounded-xl bg-white/8 border border-white/12 text-white text-sm placeholder-white/30 outline-none focus:border-blue-500 transition-colors" />
            <input placeholder="Email Address" className="px-4 py-3 rounded-xl bg-white/8 border border-white/12 text-white text-sm placeholder-white/30 outline-none focus:border-blue-500 transition-colors" />
          </div>
          <input placeholder="Company Name" className="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/12 text-white text-sm placeholder-white/30 outline-none focus:border-blue-500 transition-colors" />
          <BtnOrange full onClick={() => setDone(true)}>Request Live Demo</BtnOrange>
        </div>
      )}
    </div>
  );
};

/* ── Page ── */
const DemoPage = ({ navigate }) => {
  const [filter, setFilter] = useState("all");

  const visible = filter === "all"
    ? DEMO_VIDEOS
    : DEMO_VIDEOS.filter(v => v.cat === filter);

  return (
    <div className="page-enter">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-950 via-[#0d2151] to-[#0e2660] relative overflow-hidden pt-32 pb-20 text-center">
        <div className="hero-grid-bg absolute inset-0 opacity-50 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <SectionLabel light>Live Demos</SectionLabel>
          <h1 className="font-head font-black text-5xl md:text-6xl text-white mt-3 mb-5 tracking-tight leading-tight">
            Watch Our Software<br /><span className="gradient-text">in Action</span>
          </h1>
          <p className="text-white/60 text-xl mb-8">
            Explore interactive demos, sample reports, and real client success stories.
          </p>
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2">
            {DEMO_FILTERS.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`font-head text-sm font-semibold px-5 py-2.5 rounded-full border cursor-pointer transition-all duration-150
                  ${filter === f.id
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white/10 text-white/70 border-white/20 hover:bg-white/15 hover:text-white"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Videos */}
          <div className="mb-10">
            <SectionLabel>Sample Videos</SectionLabel>
            <h2 className="font-head font-extrabold text-3xl text-slate-900 mt-2 mb-8">
              Demo <span className="text-blue-600">Videos</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {visible.map(v => <VideoCard key={v.id} video={v} />)}
            </div>
          </div>

          {/* Interactive report */}
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8">
            <SectionLabel>Interactive Sample</SectionLabel>
            <h2 className="font-head font-extrabold text-2xl text-slate-900 mt-2 mb-2">
              Sample Interactive Report
            </h2>
            <p className="text-slate-500 text-sm">Click the chart below to simulate a live data refresh.</p>
            <MiniChart />
          </div>

          <DemoRequestForm />
        </div>
      </section>
    </div>
  );
};

export default DemoPage;
